import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const productLink = productCard?.getProductCardLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  /**
   * Refreshes cart drawer content after adding item
   * @param {Object} cartData - The cart data object
   */
  async #refreshCartDrawer(cartData) {
    const cartDrawer = document.querySelector('cart-drawer-component');
    if (!cartDrawer) {
      console.warn('[quick-add] Cart drawer not found for refresh');
      return;
    }
    
    const cartItemsComponent = cartDrawer.querySelector('cart-items-component');
    if (!cartItemsComponent || !(cartItemsComponent instanceof HTMLElement)) {
      console.warn('[quick-add] Cart items component not found for refresh');
      return;
    }
    
    console.log('[quick-add] Starting cart drawer refresh...');
    
    // Always use manual refresh for reliability
    // Section rendering API can be unreliable, so we'll use HTML fetch method
    await this.#refreshCartDrawerManually(cartDrawer, cartItemsComponent, cartData);
  }

  /**
   * Manual cart drawer refresh fallback
   * @param {Element} cartDrawer - The cart drawer element
   * @param {Element} cartItemsComponent - The cart items component
   * @param {Object} cartData - The cart data object
   */
  async #refreshCartDrawerManually(cartDrawer, cartItemsComponent, cartData) {
    try {
      console.log('[quick-add] Refreshing cart drawer manually with', cartData?.item_count || 0, 'items');
      
      const htmlResponse = await fetch('/cart?' + Date.now());
      if (!htmlResponse.ok) {
        console.warn('[quick-add] Cart HTML fetch failed:', htmlResponse.status);
        return;
      }
      
      const html = await htmlResponse.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Find the cart items wrapper in the fetched HTML
      const newCartItemsWrapper = doc.querySelector('.cart-items__wrapper') ||
                                 doc.querySelector('cart-items-component .cart-items__wrapper') ||
                                 doc.querySelector('cart-items-component');
      
      const oldWrapper = cartItemsComponent.querySelector('.cart-items__wrapper');
      
      if (newCartItemsWrapper && oldWrapper && oldWrapper instanceof HTMLElement) {
        // Get the new content HTML
        let newContent = '';
        if (newCartItemsWrapper.tagName === 'CART-ITEMS-COMPONENT') {
          const nestedWrapper = newCartItemsWrapper.querySelector('.cart-items__wrapper');
          newContent = nestedWrapper ? nestedWrapper.innerHTML : newCartItemsWrapper.innerHTML;
        } else {
          newContent = newCartItemsWrapper.innerHTML;
        }
        
        console.log('[quick-add] Updating cart items wrapper content, length:', newContent.length);
        
        // Store scroll position
        const cartDrawerItems = cartDrawer.querySelector('.cart-drawer__items');
        const previousScrollTop = cartDrawerItems ? cartDrawerItems.scrollTop : 0;
        const oldItemCount = oldWrapper.querySelector('.cart-items__list')?.children.length || 0;
        
        // Fade out old content
        oldWrapper.style.transition = 'opacity 0.2s ease';
        oldWrapper.style.opacity = '0';
        
        // Replace content after animation
        setTimeout(() => {
          if (oldWrapper && newContent) {
            oldWrapper.innerHTML = newContent;
            oldWrapper.style.opacity = '1';
            
            // Update quantity selectors with latest cart data
            if (cartData && cartData.items) {
              cartData.items.forEach((item) => {
                const variantId = item.variant_id?.toString();
                if (variantId) {
                  const selectors = document.querySelectorAll(
                    `quantity-selector-component[data-variant-id="${variantId}"], cart-quantity-selector-component[data-variant-id="${variantId}"]`
                  );
                  selectors.forEach((selector) => {
                    const input = selector.querySelector('input[data-cart-quantity]');
                    if (input) {
                      input.setAttribute('data-cart-quantity', item.quantity.toString());
                    }
                    // Update button states
                    if ('updateButtonStates' in selector && typeof selector.updateButtonStates === 'function') {
                      selector.updateButtonStates();
                    }
                    if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
                      selector.updateCartQuantity();
                    }
                  });
                }
              });
            }
            
            // Re-attach cart form handler
            // @ts-ignore - Global function
            if (typeof window.setupCartFormHandler === 'function') {
              // @ts-ignore
              window.setupCartFormHandler();
            }
            
            // Re-initialize quantity selectors
            const quantitySelectors = cartItemsComponent.querySelectorAll('quantity-selector-component, cart-quantity-selector-component');
            quantitySelectors.forEach(selector => {
              if ('connectedCallback' in selector && typeof selector.connectedCallback === 'function') {
                selector.connectedCallback();
              }
            });
            
            // Handle scroll position
            const newCartItemsList = oldWrapper.querySelector('.cart-items__list');
            const newItemCount = newCartItemsList ? newCartItemsList.children.length : 0;
            
            if (cartDrawerItems) {
              if (newItemCount > oldItemCount) {
                // Scroll to bottom to show newly added items
                setTimeout(() => {
                  if (cartDrawerItems) {
                    cartDrawerItems.scrollTop = cartDrawerItems.scrollHeight;
                    console.log('[quick-add] Scrolled to bottom to show new items');
                  }
                }, 200);
              } else {
                // Restore previous scroll position
                cartDrawerItems.scrollTop = previousScrollTop;
              }
            }
            
            console.log('[quick-add] ✓ Cart drawer refreshed successfully - old:', oldItemCount, 'new:', newItemCount);
          }
        }, 200);
      } else {
        console.warn('[quick-add] Could not find cart items wrapper');
      }
      
      // Update empty state - use the same method as ajax-cart-slider if available
      if (window.AjaxCartSlider && window.AjaxCartSlider.instance && typeof window.AjaxCartSlider.instance.updateCartDrawerEmptyState === 'function') {
        window.AjaxCartSlider.instance.updateCartDrawerEmptyState(cartData);
      } else {
        // Fallback: manual empty state update
        const dialog = cartDrawer.querySelector('.cart-drawer__dialog');
        const emptyHeading = cartDrawer.querySelector('.cart-drawer__heading--empty');
        const emptyHeader = emptyHeading?.closest('.cart-drawer__header');
        const normalHeading = cartDrawer.querySelector('.cart-drawer__heading:not(.cart-drawer__heading--empty)');
        const normalHeader = normalHeading?.closest('.cart-drawer__header');
        
        if (dialog && cartData) {
          if (cartData.item_count === 0) {
            dialog.classList.add('cart-drawer--empty');
            dialog.setAttribute('aria-labelledby', 'cart-drawer-heading-empty');
            if (emptyHeader) emptyHeader.style.display = 'flex';
            if (emptyHeading) emptyHeading.style.display = 'block';
            if (normalHeader) normalHeader.style.display = 'none';
            if (normalHeading) normalHeading.style.display = 'none';
          } else {
            dialog.classList.remove('cart-drawer--empty');
            dialog.setAttribute('aria-labelledby', 'cart-drawer-heading');
            if (emptyHeader) emptyHeader.style.display = 'none';
            if (emptyHeading) emptyHeading.style.display = 'none';
            if (normalHeader) normalHeader.style.display = 'flex';
            if (normalHeading) normalHeading.style.display = 'block';
          }
        }
      }
    } catch (error) {
      console.error('[quick-add] Error in manual cart refresh:', error);
    }
  }



  connectedCallback() {
    super.connectedCallback();
    
    // Handle click on "choose" button to open modal for multi-variant products
    const chooseButton = this.querySelector('[data-quick-add-choose]');
    if (chooseButton) {
      chooseButton.addEventListener('click', async (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        // Fetch product page and open modal
        const productCard = this.closest('product-card');
        if (productCard && 'productPageUrl' in productCard) {
          const productPageUrl = productCard.productPageUrl;
          if (productPageUrl) {
            try {
              const html = await this.fetchProductPage(productPageUrl);
              if (html) {
                const productGrid = html.querySelector('[product-grid-view], .product-grid, main');
                if (productGrid) {
                  await this.updateQuickAddModal(productGrid);
                  this.#openQuickAddModal();
                }
              }
            } catch (error) {
              console.error('[quick-add] Error opening modal:', error);
            }
          }
        }
      });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
  }


  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) {
      console.warn('Quick-add: Missing productGrid or modalContent', { productGrid, modalContent });
      return;
    }
    
    // Ensure we have content to display
    if (!productGrid.children.length && !productGrid.querySelector('product-form-component, variant-picker, .product-details')) {
      console.warn('Quick-add: Product grid appears to be empty', productGrid);
      // Try to fetch again or show error
      return;
    }

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = (this instanceof HTMLElement && this.dataset.productTitle) || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
    
    // Open cart drawer after closing quick add dialog
    setTimeout(() => {
      const cartDrawer = document.querySelector('cart-drawer-component');
      if (cartDrawer && 'open' in cartDrawer && typeof cartDrawer.open === 'function') {
        cartDrawer.open();
      } else {
        // Fallback: trigger cart drawer via event or click
        const cartDrawerTrigger = document.querySelector('.cart-drawer-trigger, [data-cart-trigger]');
        if (cartDrawerTrigger && cartDrawerTrigger instanceof HTMLElement) {
          cartDrawerTrigger.click();
        } else {
          // Try to dispatch event that ajax-cart-slider listens for
          const cartAddEvent = new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
              resource: {},
              sourceId: null,
              data: {
                source: 'quick-add-dialog',
                itemCount: event.detail.data.itemCount || 1
              }
            }
          });
          document.dispatchEvent(cartAddEvent);
        }
      }
    }, 100);
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
